// /proyectos/:id/encuestas — las encuestas del propietario del proyecto
// (ADR-0153, fase 3, corte 1, #0750). Hermana de consultas y avisos: mismo
// encabezado del proyecto, KPIs densos, paneles y tabla paginada en el
// cliente, sobre `GET /postventa-dashboard/survey-summary?assetGroupId=`.
//
// Quién la ve lo decide el servidor (jefe_proyecto en sus proyectos,
// gerente_postventa en toda la org; 403/404 para el resto). Un número
// sólo con 5 respuestas o más: con menos, «Pocas respuestas», también en
// las distribuciones (sus barras dirían el número que la tile oculta). Las
// respuestas (con o sin comentario: un detractor que no escribió también
// se sigue) llevan la unidad, nunca el nombre del propietario.
//
// El promedio por contratista es una evaluación de un tercero (ADR-0153):
// se muestra para seguimiento y nunca ordena ni asigna trabajo.
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import type {
  PostventaNpsWaveSummary,
  PostventaSurveySummaryResponse,
} from "@bitacorapro/api-types";
import { EmptyState } from "../components/EmptyState";
import { Icon } from "../components/icons";
import { KpiTile } from "../components/KpiTile";
import { PageError } from "../components/PageError";
import { PageSkeleton } from "../components/PageSkeleton";
import { Pager } from "../components/Pager";
import { Skel } from "../components/Skel";
import { ApiError } from "../lib/api/error";
import { useCarteraProyectoDetail, usePostventaSurveySummary, useRecipe } from "../lib/api/queries";
import {
  bucketsTexto,
  distribucion,
  distribucionVisible,
  fechaRespuesta,
  npsTone,
  npsValor,
  POCAS_RESPUESTAS,
  promedioValor,
  respuestasHint,
  SURVEY_KIND_LABEL,
  sumarOlas,
  tasaRespuesta,
  totalRespuestasTexto,
  visitaTone,
  type BarTone,
} from "../lib/postventa/encuestas";
import { fechaCorta } from "../lib/postventa/observacionesList";

const POR_PAGINA = 10;
const SUB =
  "Qué tan satisfechos están los propietarios: el NPS a los 2 y a los 9 meses de la entrega y la encuesta de cada visita técnica.";

export function EncuestasDelProyectoRoute() {
  const { id } = useParams<{ id: string }>();
  const { data: recipe } = useRecipe();
  const detail = useCarteraProyectoDetail(id, { limit: 1 });
  const summary = usePostventaSurveySummary(id, id !== undefined);

  const groupLabel = recipe?.labels.assetGroup ?? "Proyecto";
  const groupPlural = recipe?.labels.assetGroupPlural ?? "Proyectos";
  const error = detail.error ?? summary.error;

  if (error) {
    if (error instanceof ApiError && (error.status === 404 || error.status === 403)) {
      return (
        <div className="page-pad">
          <h1 className="page-header__title">{groupLabel} no encontrado</h1>
          <p className="page-header__sub">
            El id <code>{id}</code> no corresponde a ningún {groupLabel.toLowerCase()} que puedas
            ver.
          </p>
          <Link to="/proyectos" className="btn btn--secondary">
            <Icon.Arrow s={14} /> Volver a {groupPlural.toLowerCase()}
          </Link>
        </div>
      );
    }
    return (
      <div className="page">
        <Encabezado groupPlural={groupPlural} id={id ?? ""} name={null} />
        <div className="panel">
          <PageError
            title="No pudimos cargar las encuestas"
            description="Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
            onRetry={() => {
              void detail.refetch();
              void summary.refetch();
            }}
          />
        </div>
      </div>
    );
  }

  if (!detail.data || !summary.data || !id) {
    return (
      <div className="page">
        <Encabezado groupPlural={groupPlural} id={id ?? ""} name={null} />
        <PageSkeleton label="Cargando encuestas…">
          <div className="kpi-grid" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="kpi-tile kpi-tile--dense">
                <div className="skel-row">
                  <Skel width="45%" height={9} />
                  <Skel width="32%" height={22} />
                  <Skel width="60%" height={9} />
                </div>
              </div>
            ))}
          </div>
        </PageSkeleton>
      </div>
    );
  }

  const { group } = detail.data;
  return (
    <div className="page">
      <Encabezado
        groupPlural={groupPlural}
        id={id}
        name={group.subline ? `${group.name} — ${group.subline}` : group.name}
      />
      {summary.data.enabled ? (
        <Panel summary={summary.data} />
      ) : (
        <div className="panel">
          <EmptyState
            title="Sin encuestas"
            description={`Este ${groupLabel.toLowerCase()} no tiene encuestas a propietarios.`}
          />
        </div>
      )}
    </div>
  );
}

function Encabezado({
  groupPlural,
  id,
  name,
}: {
  groupPlural: string;
  id: string;
  name: string | null;
}) {
  return (
    <div className="page-header">
      <div className="page-header__lead">
        <div className="page-header__eyebrow">
          <Link to="/proyectos">{groupPlural}</Link> /{" "}
          {name ? <Link to={`/proyectos/${id}`}>{name}</Link> : <Skel width={140} height={10} />}
        </div>
        <h1 className="page-header__title" tabIndex={-1}>
          Encuestas
        </h1>
        <p className="page-header__sub">{SUB}</p>
      </div>
      <div className="page-header__actions">
        <Link to={`/proyectos/${id}`} className="btn btn--secondary btn--sm">
          Ver unidades
        </Link>
      </div>
    </div>
  );
}

function Panel({ summary }: { summary: PostventaSurveySummaryResponse }) {
  const [pagina, setPagina] = useState(1);
  const { nps2m, nps9m, visita, porContratista, respuestas, respuestasTotal } = summary;
  const total = sumarOlas(nps2m, nps9m);
  const tasa = tasaRespuesta(total.respondidas, total.enviadas);
  const pages = Math.max(1, Math.ceil(respuestas.length / POR_PAGINA));
  const actual = Math.min(pagina, pages);
  const filas = respuestas.slice((actual - 1) * POR_PAGINA, actual * POR_PAGINA);

  return (
    <>
      <section className="kpi-grid" aria-label="Resumen" style={{ marginBottom: 20 }}>
        <KpiTile
          dense
          label={SURVEY_KIND_LABEL.nps_2m}
          value={npsValor(nps2m.nps)}
          hint={respuestasHint(nps2m.respondidas, nps2m.enviadas)}
        />
        <KpiTile
          dense
          label={SURVEY_KIND_LABEL.nps_9m}
          value={npsValor(nps9m.nps)}
          hint={respuestasHint(nps9m.respondidas, nps9m.enviadas)}
        />
        <KpiTile
          dense
          label="Tasa de respuesta"
          value={tasa === null ? "—" : tasa}
          {...(tasa === null ? {} : { unit: "%" })}
          hint="De las encuestas NPS enviadas"
        />
        <KpiTile
          dense
          label={SURVEY_KIND_LABEL.visita}
          value={promedioValor(visita.promedio)}
          {...(visita.promedio === null ? {} : { unit: "de 5" })}
          hint={respuestasHint(visita.respondidas, visita.enviadas)}
        />
      </section>

      <div className="encuestas-dist" style={{ marginBottom: 20 }}>
        <Distribucion titulo="Distribución a los 2 meses" ola={nps2m} />
        <Distribucion titulo="Distribución a los 9 meses" ola={nps9m} />
        <div className="panel">
          <div className="panel__head">
            <div className="panel__title">Visita técnica</div>
            <div className="panel__sub">De 1 (muy mala) a 5 (muy buena)</div>
          </div>
          {distribucionVisible(visita.respondidas) ? (
            <Barras dist={visita.dist} first={1} tone={visitaTone} />
          ) : (
            <PocasRespuestas respondidas={visita.respondidas} />
          )}
        </div>
      </div>

      <div className="panel" style={{ marginBottom: 20 }}>
        <div className="panel__head">
          <div className="panel__title">Visita técnica por contratista</div>
          <div className="panel__sub">
            Para seguimiento: no se usa para asignar trabajo. Con menos de 5 respuestas no se
            muestra el promedio.
          </div>
        </div>
        {porContratista.length === 0 ? (
          <EmptyState title="Aún no hay encuestas de visita respondidas" />
        ) : (
          <div className="dtable-scroll" tabIndex={0} role="region" aria-label="Por contratista">
            <table className="dtable dtable--compact dtable--no-check">
              <caption className="sr-only">Encuesta de la visita técnica por contratista</caption>
              <thead>
                <tr>
                  <th scope="col">Contratista</th>
                  <th scope="col">Respuestas</th>
                  <th scope="col">Promedio</th>
                </tr>
              </thead>
              <tbody>
                {porContratista.map((p) => (
                  <tr key={p.providerId}>
                    <td className="cell-strong">{p.shortName}</td>
                    <td className="mono">{p.respondidas}</td>
                    <td>
                      {p.promedio === null ? (
                        <span className="cell-meta">{POCAS_RESPUESTAS}</span>
                      ) : (
                        <span className="mono">{promedioValor(p.promedio)} de 5</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="panel">
        <div className="panel__head">
          <div className="panel__title">Respuestas</div>
          <div className="panel__sub mono">
            {totalRespuestasTexto(respuestas.length, respuestasTotal)}
          </div>
        </div>
        {respuestas.length === 0 ? (
          <EmptyState title="Aún no hay respuestas" />
        ) : (
          <>
            <div className="dtable-scroll" tabIndex={0} role="region" aria-label="Respuestas">
              <table className="dtable dtable--compact dtable--no-check">
                <caption className="sr-only">Respuestas de los propietarios</caption>
                <thead>
                  <tr>
                    <th scope="col">Unidad</th>
                    <th scope="col">Encuesta</th>
                    <th scope="col">Puntaje</th>
                    <th scope="col" className="col-title">
                      Comentario
                    </th>
                    <th scope="col">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.map((c) => (
                    <tr key={c.surveyId}>
                      <td className="cell-strong">{c.assetUnit ?? c.assetLabel}</td>
                      <td className="cell-meta">{SURVEY_KIND_LABEL[c.kind]}</td>
                      <td className="mono">
                        {c.score} de {c.kind === "visita" ? 5 : 10}
                      </td>
                      {c.comment === null ? (
                        <td className="col-title cell-meta">Sin comentario</td>
                      ) : (
                        <td className="col-title encuestas-comment">{c.comment}</td>
                      )}
                      <td className="mono">{fechaCorta(fechaRespuesta(c))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pages > 1 && (
              <Pager
                currentPage={actual}
                totalPages={pages}
                totalItems={respuestas.length}
                pageSize={POR_PAGINA}
                onPage={setPagina}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}

function PocasRespuestas({ respondidas }: { respondidas: number }) {
  return (
    <EmptyState
      title={POCAS_RESPUESTAS}
      description={`Con ${respondidas === 1 ? "1 respuesta" : `${respondidas} respuestas`} no se muestra la distribución: se ve desde 5.`}
    />
  );
}

function Distribucion({ titulo, ola }: { titulo: string; ola: PostventaNpsWaveSummary }) {
  return (
    <div className="panel">
      <div className="panel__head">
        <div className="panel__title">{titulo}</div>
        {distribucionVisible(ola.respondidas) && (
          <div className="panel__sub">{bucketsTexto(ola)}</div>
        )}
      </div>
      {distribucionVisible(ola.respondidas) ? (
        <Barras dist={ola.dist} first={0} tone={npsTone} />
      ) : (
        <PocasRespuestas respondidas={ola.respondidas} />
      )}
    </div>
  );
}

function Barras({
  dist,
  first,
  tone,
}: {
  dist: readonly number[];
  first: number;
  tone: (score: number) => BarTone;
}) {
  return (
    <ul className="encuestas-bars" aria-label="Respuestas por puntaje">
      {distribucion(dist, first).map((r) => (
        <li key={r.score} className="bar-row">
          <span className="bar-row__label">{r.score}</span>
          <span className="bar-track" aria-hidden="true">
            <span
              className={`bar-fill bar-fill--${tone(r.score)} encuestas-bars__fill`}
              style={{ width: `${r.pct}%` }}
            />
          </span>
          <span className="bar-row__num">
            {r.n}
            <span className="sr-only"> respuestas con {r.score}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}
